// pages/TradeIn/SubmitTradeIn.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../components/Theme/ThemeProvider';
import { summaryApi } from '../../common';
import { toast } from 'react-toastify';
import { ArrowLeft, X, Plus } from 'lucide-react';
import uploadImage from '../../helpers/uploadImage';
import { authFetch } from '../../helpers/authFetch';

function SubmitTradeIn() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    phoneBrand: '',
    phoneModel: '',
    phoneCondition: 'Good',
    storage: '',
    color: '',
    purchaseYear: new Date().getFullYear(),
    originalPrice: '',
    phoneImages: [],
    description: '',
    physicalDamage: false,
    screenCondition: 'Perfect',
    batteryHealth: 'Good (80-100%)',
    accessories: {
      hasBox: false,
      hasCharger: false,
      hasEarphones: false
    }
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: checked
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const uploadResult = await uploadImage(file);
      
      setFormData(prev => ({
        ...prev,
        phoneImages: [...prev.phoneImages, uploadResult.secure_url]
      }));
      
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      phoneImages: prev.phoneImages.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.phoneImages.length === 0) {
      toast.error('Please upload at least one image');
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(summaryApi.submitTradeIn.url, {
        method: summaryApi.submitTradeIn.method,
        
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Trade-in request submitted successfully!');
        navigate('/my-tradeins');
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Failed to submit trade-in request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16" style={{ backgroundColor: colors.bg }}>
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg transition-all hover:scale-110"
            style={{
              backgroundColor: colors.bgSecondary,
              color: colors.primary
            }}
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-4xl font-bold" style={{ color: colors.text }}>
              Trade In Your Device
            </h1>
            <p style={{ color: colors.textSecondary }}>
              Get instant value for your old phone
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Device Information */}
          <div
            className="p-6 rounded-xl"
            style={{
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${colors.border}`
            }}
          >
            <h2 className="text-xl font-bold mb-6" style={{ color: colors.text }}>
              Device Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                  Brand *
                </label>
                <input
                  type="text"
                  name="phoneBrand"
                  value={formData.phoneBrand}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Apple, Samsung"
                  className="w-full px-4 py-3 rounded-lg outline-none"
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    border: `2px solid ${colors.border}`
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                  Model *
                </label>
                <input
                  type="text"
                  name="phoneModel"
                  value={formData.phoneModel}
                  onChange={handleChange}
                  required
                  placeholder="e.g., iPhone 13 Pro, Galaxy S21"
                  className="w-full px-4 py-3 rounded-lg outline-none"
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    border: `2px solid ${colors.border}`
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                  Storage *
                </label>
                <select
                  name="storage"
                  value={formData.storage}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg outline-none"
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    border: `2px solid ${colors.border}`
                  }}
                >
                  <option value="">Select Storage</option>
                  <option value="32GB">32GB</option>
                  <option value="64GB">64GB</option>
                  <option value="128GB">128GB</option>
                  <option value="256GB">256GB</option>
                  <option value="512GB">512GB</option>
                  <option value="1TB">1TB</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                  Color
                </label>
                <input
                  type="text"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  placeholder="e.g., Black, White"
                  className="w-full px-4 py-3 rounded-lg outline-none"
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    border: `2px solid ${colors.border}`
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                  Purchase Year
                </label>
                <input
                  type="number"
                  name="purchaseYear"
                  value={formData.purchaseYear}
                  onChange={handleChange}
                  min="2010"
                  max={new Date().getFullYear()}
                  className="w-full px-4 py-3 rounded-lg outline-none"
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    border: `2px solid ${colors.border}`
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                  Original Price (₦)
                </label>
                <input
                  type="number"
                  name="originalPrice"
                  value={formData.originalPrice}
                  onChange={handleChange}
                  placeholder="0"
                  className="w-full px-4 py-3 rounded-lg outline-none"
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    border: `2px solid ${colors.border}`
                  }}
                />
              </div>
            </div>
          </div>

          {/* Condition Details */}
          <div
            className="p-6 rounded-xl"
            style={{
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${colors.border}`
            }}
          >
            <h2 className="text-xl font-bold mb-6" style={{ color: colors.text }}>
              Condition Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                  Overall Condition *
                </label>
                <select
                  name="phoneCondition"
                  value={formData.phoneCondition}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg outline-none"
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    border: `2px solid ${colors.border}`
                  }}
                >
                  <option value="Excellent">Excellent</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Poor">Poor</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                  Screen Condition
                </label>
                <select
                  name="screenCondition"
                  value={formData.screenCondition}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg outline-none"
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    border: `2px solid ${colors.border}`
                  }}
                >
                  <option value="Perfect">Perfect</option>
                  <option value="Minor Scratches">Minor Scratches</option>
                  <option value="Cracked">Cracked</option>
                  <option value="Broken">Broken</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                  Battery Health
                </label>
                <select
                  name="batteryHealth"
                  value={formData.batteryHealth}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg outline-none"
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    border: `2px solid ${colors.border}`
                  }}
                >
                  <option value="Good (80-100%)">Good (80-100%)</option>
                  <option value="Fair (60-79%)">Fair (60-79%)</option>
                  <option value="Poor (Below 60%)">Poor (Below 60%)</option>
                </select>
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="physicalDamage"
                    checked={formData.physicalDamage}
                    onChange={handleChange}
                    className="w-5 h-5"
                  />
                  <span className="text-sm font-semibold" style={{ color: colors.text }}>
                    Has Physical Damage
                  </span>
                </label>
              </div>
            </div>

            {/* Accessories */}
            <div className="mt-6">
              <label className="block text-sm font-semibold mb-3" style={{ color: colors.text }}>
                Included Accessories
              </label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="accessories.hasBox"
                    checked={formData.accessories.hasBox}
                    onChange={handleChange}
                    className="w-5 h-5"
                  />
                  <span style={{ color: colors.text }}>Original Box</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="accessories.hasCharger"
                    checked={formData.accessories.hasCharger}
                    onChange={handleChange}
                    className="w-5 h-5"
                  />
                  <span style={{ color: colors.text }}>Charger</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="accessories.hasEarphones"
                    checked={formData.accessories.hasEarphones}
                    onChange={handleChange}
                    className="w-5 h-5"
                  />
                  <span style={{ color: colors.text }}>Earphones</span>
                </label>
              </div>
            </div>
          </div>

          {/* Images */}
          <div
            className="p-6 rounded-xl"
            style={{
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${colors.border}`
            }}
          >
            <h2 className="text-xl font-bold mb-6" style={{ color: colors.text }}>
              Device Images *
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {formData.phoneImages.map((image, index) => (
                <div key={index} className="relative group">
                  <img
                    src={image}
                    alt={`Device ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 p-1 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}

              <label
                className="h-32 rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all hover:scale-105"
                style={{ borderColor: colors.border }}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  className="hidden"
                />
                {uploadingImage ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-transparent" style={{ borderTopColor: colors.primary }} />
                ) : (
                  <>
                    <Plus size={32} style={{ color: colors.primary }} />
                    <span className="text-xs mt-2" style={{ color: colors.textSecondary }}>
                      Add Image
                    </span>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Description */}
          <div
            className="p-6 rounded-xl"
            style={{
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${colors.border}`
            }}
          >
            <label className="block text-sm font-semibold mb-3" style={{ color: colors.text }}>
              Additional Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={6}
              placeholder="Describe any additional details about your device..."
              className="w-full px-4 py-3 rounded-lg outline-none resize-none"
              style={{
                backgroundColor: colors.bg,
                color: colors.text,
                border: `2px solid ${colors.border}`
              }}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || uploadingImage}
            className="w-full py-4 rounded-lg font-bold text-white text-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
            }}
          >
            {loading ? 'Submitting...' : 'Submit Trade-In Request'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default SubmitTradeIn;
